import { memo, useState } from 'react';

import styled, { css } from 'styled-components';

import { arrowTransition } from 'components/templates/header/web/menu/RightSubMenu';

import { clickToGTM } from 'utils/seo/seoGTMUtil';

import { getIconPositionStyle } from 'styles/components/getPositionStyle';
import { getShapeStyle } from 'styles/components/getShapeStyle';
import { getSizeStyle } from 'styles/components/getSizeStyle';
import { getVariantButtonStyle } from 'styles/components/getVariantStyle';

/**
 * @desc 버튼 컴포넌트
 * @param {string} type - 버튼 타입 (e.g., submit, button)
 * @param {string} className - 추가적인 클래스명
 * @param {function} onClick - 클릭 이벤트 핸들러
 * @param {JSX.Element} icon - 아이콘 엘리먼트
 * @param {JSX.Element} beforeIcon - 아이콘 (텍스트 앞에 위치)
 * @param {boolean} disabled - 버튼 비활성화 여부
 * @param {Object} buttonStyle - 추가적인 스타일
 * @param {string} tooltip - 툴팁 텍스트
 * @param {string} tooltipPosition - 툴팁 위치
 * @returns {JSX.Element} 버튼 컴포넌트
 */
const Button = ({
  type,
  className,
  variant,
  size = 'medium',
  shape = 'round',
  position = 'left',
  onClick,
  onMouseEnter,
  onMouseLeave,
  icon,
  children,
  beforeIcon,
  disabled,
  buttonStyle,
  tooltip,
  tooltipPosition = 'right', // 툴팁 위치를 위한 prop 추가
  gtm,
  afterIcon,
  as,
  href,
  download,
  target,
}) => {
  const [showTooltip, setShowTooltip] = useState(false); // 툴팁 표시 상태 관리

  // 툴팁 마우스 오버 핸들러
  const handleMouseEnter = () => {
    if (tooltip) {
      setShowTooltip(true);
    }
    if (onMouseEnter) {
      onMouseEnter();
    }
  };

  // 툴팁 마우스 리브 핸들러
  const handleMouseLeave = () => {
    if (tooltip) {
      setShowTooltip(false);
    }
    if (onMouseLeave) {
      onMouseLeave();
    }
  };

  return (
    <ButtonStyle
      type={type}
      className={className && className !== 'undefined' ? className : undefined}
      variant={variant}
      size={size}
      shape={shape}
      position={position}
      onClick={(e) => {
        gtm && clickToGTM(gtm);
        if (onClick) {
          return onClick(e);
        }
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      disabled={disabled}
      data-name={String(gtm?.id || 'button')} // ✅ 항상 문자열
      data-action="click"
      aria-label={gtm?.target ?? '버튼'}
      style={{ ...buttonStyle }}
      as={as || (href ? 'a' : 'button')}
      href={href}
      download={download}
      target={target}
      rel={target === '_blank' ? 'noopener noreferrer' : undefined}
    >
      {/* 아이콘과 텍스트 렌더링 */}
      {beforeIcon && <>{beforeIcon}</>}
      {children}
      {icon && <>{icon}</>}
      {afterIcon && <>{afterIcon}</>}

      {/* 툴팁 표시 */}
      {showTooltip && (
        <Tooltip position={tooltipPosition} className="tooltip">
          {tooltip}
        </Tooltip>
      )}
    </ButtonStyle>
  );
};

// 버튼 스타일 객체 리터럴
const buttonClassName = {};

const ButtonStyle = styled.button`
  position: relative; /* 툴팁을 위한 상대적 위치 설정 */
  height: 40px;
  white-space: nowrap;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;

  svg {
    font-size: 16px;
    width: 38px;
  }

  ${({ size }) => getSizeStyle(size)}
  ${({ variant }) => getVariantButtonStyle(variant)}
  ${({ shape }) => getShapeStyle(shape)}
  ${({ position }) => getIconPositionStyle(position)}
  ${({ fontSize }) =>
    fontSize &&
    css`
      font-size: ${fontSize};
    `}

  &:disabled,
  &.disabled {
    ${getVariantButtonStyle('disabled')}
  }

  &.w-full {
    width: 100%;
  }

  &.icon {
    font-size: 14px;
    gap: 6px;
  }

  // 각 조건에 따른 버튼 그룹 스타일 적용
  ${({ className }) => buttonClassName[className] || null}

  ${arrowTransition}
`;

// 버튼 위치 스타일 객체 리터럴
export const buttonPositionStyles = {
  right: css`
    left: 100%;
    margin-left: 8px;
  `,
  left: css`
    right: 100%;
    margin-right: 8px;
  `,
};

// 버튼 툴팁을 위한 스타일
export const buttonTooltip = css`
  border: 1px solid ${({ theme }) => theme.color.gray200};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  background: ${({ theme }) => theme.color.white};
  color: ${({ theme }) => theme.color.primaryDark};
`;

export const Tooltip = styled.div`
  position: absolute;
  padding: 4px 8px;
  text-align: left;
  border-radius: 4px;
  font-size: 12px;
  top: 50%; /* 버튼 중앙에 위치 */
  transform: translateY(-50%);
  z-index: 100;
  ${buttonTooltip}

  // 툴팁 위치에 따라 스타일 적용
  ${({ position }) => buttonPositionStyles[position] || ''}
`;
export default memo(Button);
